import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Sparkles, TrendingUp, MessageSquare, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SAT_LABELS = {
  yes: "Parfaitement",
  partial: "Partiellement",
  no: "Non",
  final: "Feedback final",
};
const SAT_COLORS = {
  yes: "bg-emerald-500/100",
  partial: "bg-amber-500/100",
  no: "bg-rose-500/100",
  final: "bg-emerald-500/100",
};

export default function AdminOnboardingInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/onboarding/insights");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accès refusé");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runImprovement = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/onboarding/improve");
      toast.success("Nouveau plan d'amélioration généré");
      setData((d) => ({ ...(d || {}), latest_plan: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur LLM");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const total = data?.total_feedback || 0;
  const byStep = data?.by_step || {};
  const recentComments = data?.recent_comments || [];
  const latestPlan = data?.latest_plan?.plan;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 to-white pb-16">
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-emerald-500/30">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center gap-3">
          <Link to="/admin" className="w-10 h-10 rounded-full border border-[var(--border)] grid place-items-center active:scale-95">
            <ChevronLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Auto-amélioration</p>
            <p className="font-display font-bold text-base text-[var(--text)]">Insights Onboarding</p>
          </div>
          <button
            onClick={load}
            className="w-10 h-10 rounded-full border border-[var(--border)] grid place-items-center active:scale-95"
            data-testid="refresh-insights"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-6 space-y-6">
        {/* Top stat */}
        <div className="rounded-3xl border-2 border-emerald-500/40 bg-emerald-500/10/60 p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 grid place-items-center">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">Retours collectés</p>
            <p className="font-display text-3xl font-black text-[var(--text)]">{total}</p>
          </div>
        </div>

        {/* Distribution par étape */}
        <div className="bg-[var(--surface)] rounded-xl border border-emerald-500/30 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <h2 className="font-bold text-[var(--text)]">Satisfaction par étape</h2>
          </div>
          {Object.keys(byStep).length === 0 ? (
            <p className="text-sm text-zinc-400">Aucune donnée pour l'instant.</p>
          ) : (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].filter((s) => byStep[s]).map((s) => {
                const row = byStep[s];
                const rowTotal = row.total || 1;
                return (
                  <div key={s} data-testid={`step-row-${s}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-bold">Étape {s}</p>
                      <p className="text-xs text-zinc-400">{row.total} retour{row.total > 1 ? "s" : ""}</p>
                    </div>
                    <div className="flex h-3 rounded-full overflow-hidden bg-black/5">
                      {["yes", "partial", "no", "final"].map((sat) => {
                        const v = row[sat] || 0;
                        if (!v) return null;
                        return (
                          <div
                            key={sat}
                            className={SAT_COLORS[sat]}
                            style={{ width: `${(v / rowTotal) * 100}%` }}
                            title={`${SAT_LABELS[sat]}: ${v}`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex gap-3 mt-1.5 flex-wrap">
                      {["yes", "partial", "no", "final"].map((sat) => {
                        const v = row[sat] || 0;
                        if (!v) return null;
                        return (
                          <span key={sat} className="text-[11px] text-zinc-400 flex items-center gap-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${SAT_COLORS[sat]}`} />
                            {SAT_LABELS[sat]} {v}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Plan d'amélioration IA */}
        <div className="bg-black rounded-3xl p-5 text-white">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-300" />
              <h2 className="font-bold">Plan d'auto-amélioration</h2>
            </div>
            <button
              onClick={runImprovement}
              disabled={generating || total === 0}
              data-testid="generate-plan-btn"
              className="bg-emerald-500/100 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-full px-4 py-2 text-xs font-bold active:scale-95 transition-all flex items-center gap-1.5"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {generating ? "Prosperr analyse…" : "Générer un plan"}
            </button>
          </div>
          {!latestPlan ? (
            <p className="text-sm text-white/60 leading-relaxed">
              {total === 0
                ? "Aucun retour pour l'instant. Le plan sera disponible dès le premier feedback."
                : "Lancez une analyse : Prosperr étudie les retours et propose des améliorations concrètes."}
            </p>
          ) : (
            <div className="space-y-4 text-sm">
              {latestPlan.health && (
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-1.5">Santé</p>
                  <p className="text-white/85 leading-relaxed">{latestPlan.health}</p>
                </div>
              )}
              {Array.isArray(latestPlan.pain_points) && latestPlan.pain_points.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-2">Points de friction</p>
                  <ul className="space-y-1.5">
                    {latestPlan.pain_points.map((p, i) => (
                      <li key={i} className="text-white/75 text-sm flex gap-2">
                        <span className="text-emerald-400">•</span>{p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(latestPlan.recommendations) && latestPlan.recommendations.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-2">Recommandations</p>
                  <div className="space-y-2">
                    {latestPlan.recommendations.map((r, i) => (
                      <div key={i} className="bg-white/5 rounded-2xl p-3.5 border border-white/10" data-testid={`reco-${i}`}>
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                            r.priority === "P0" ? "bg-rose-500/100/30 text-rose-200" :
                            r.priority === "P1" ? "bg-amber-500/100/30 text-amber-200" :
                            "bg-emerald-500/100/30 text-emerald-200"
                          }`}>{r.priority || "P2"}</span>
                          <span className="text-[10px] text-white/50 uppercase tracking-widest">Étape {r.step} · {r.type}</span>
                        </div>
                        <p className="font-semibold text-white">{r.suggestion}</p>
                        {r.target && <p className="text-xs text-white/55 mt-0.5">Cible : {r.target}</p>}
                        {r.rationale && <p className="text-xs text-white/65 mt-1.5 italic">{r.rationale}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {latestPlan.next_experiment && (
                <div className="bg-emerald-500/100/15 rounded-2xl p-4 border border-emerald-500/30">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-1.5">Expérimentation suivante</p>
                  <p className="text-white/90 text-sm">{latestPlan.next_experiment}</p>
                </div>
              )}
              <p className="text-[11px] text-white/40">
                Échantillon : {data.latest_plan?.sample_size} · Généré {data.latest_plan?.generated_at?.slice(0, 16).replace("T", " à ")}
              </p>
            </div>
          )}
        </div>

        {/* Derniers commentaires */}
        <div className="bg-[var(--surface)] rounded-xl border border-emerald-500/30 p-5 shadow-sm">
          <h2 className="font-bold text-[var(--text)] mb-3">Derniers retours</h2>
          {recentComments.length === 0 ? (
            <p className="text-sm text-zinc-400">Aucun commentaire encore.</p>
          ) : (
            <div className="space-y-3">
              {recentComments.map((c, i) => (
                <div key={i} className="border border-emerald-500/30 rounded-2xl p-3.5" data-testid={`comment-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`w-2 h-2 rounded-full ${SAT_COLORS[c.satisfaction]}`} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      Étape {c.step} · {SAT_LABELS[c.satisfaction]}
                    </span>
                    <span className="text-[10px] text-zinc-500 ml-auto">
                      {c.created_at?.slice(0, 10)}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed">{c.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
